import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  Grid,
  makeStyles,
  MenuItem,
} from "@material-ui/core";
import clsx from "clsx";
import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import ReactModal from "react-modal";

import { AddIcon } from "../../../components/icons/themes/windows10";

import useOnClickOutside from "../../../hooks/on-click-outside";
import { useOnPressEnter } from "../../../hooks/on-key-press";
import { theme } from "../../../theme";

const useStyles = makeStyles((theme) => ({
  cancelBtn: {
    background: theme.palette.background.default,
  },
  card: {
    width: "100%",
  },
  cardActions: {
    padding: theme.spacing(2),
  },
  cardContent: {
    padding: theme.spacing(2),
  },
  cardHeader: {
    backgroundColor: theme.palette.grey[50],
    borderBottomColor: theme.palette.grey[200],
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
    padding: theme.spacing(2),
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  modalActions: {
    alignContent: "center",
    justifyContent: "right",
  },
}));

const customModalStyles = {
  content: {
    zIndex: 10050,
    minHeight: `20vh`,
    minWidth: "20em",
    maxHeight: 700,
    maxWidth: "30em",
    top: "auto",
    bottom: "auto",
    right: "auto",
    left: "auto",
    position: "static" as "static",
    display: "flex",
    padding: 0,
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, .5)",
    zIndex: 10000,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};

export type EditTagsModalProps = {
  numEvents: number;
  onClose: () => void;
  onSave: (tags: Record<string, true>) => void;
  prevAllTags: Record<string, true>;
  prevSetTags: Record<string, true>;
};

export const EditTagsModal: React.FC<EditTagsModalProps> = ({
  prevAllTags,
  prevSetTags,
  onClose,
  onSave,
  numEvents,
}) => {
  const classes = useStyles();

  const [allTags, setAllTags] = useState<Record<string, true>>(prevAllTags);
  const [activeTags, setActiveTags] = useState<Record<string, true>>(
    prevSetTags
  );
  const [newTagName, setNewTagName] = useState("");
  const [addingNewTag, setAddingNewTag] = useState(false);
  const addingNewTagRef = useRef<HTMLInputElement | null>(null);

  const focusAddNewField = () => {
    if (addingNewTag && addingNewTagRef && addingNewTagRef.current) {
      addingNewTagRef.current.focus();
      addingNewTagRef.current.select();
    }
  };

  const maybeCancelAddingNewTag = () => {
    if (!newTagName) {
      setAddingNewTag(false);
    } else {
      addNewTag();
    }
  };

  const addNewTag = () => {
    setAllTags(_.extend({}, allTags, { [newTagName]: true }));
    setActiveTags(_.extend({}, activeTags, { [newTagName]: true }));
    setAddingNewTag(false);
  };

  useEffect(focusAddNewField, [addingNewTag, addingNewTagRef]);
  useOnClickOutside([addingNewTagRef], maybeCancelAddingNewTag);
  useOnPressEnter([addingNewTagRef], () => {
    if (newTagName) {
      addNewTag();
    }
  });

  function handleSave() {
    onSave(activeTags);
  }

  const tagRows: string[] = _(allTags)
    .keys()
    .orderBy((tag) => _.lowerCase(tag))
    .value();

  return (
    <ReactModal
      appElement={document.getElementById("root") as HTMLElement}
      isOpen={true}
      onRequestClose={onClose}
      style={customModalStyles}
      contentLabel="Edit Tags"
    >
      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeader}
          title={`Edit Tags for ${
            numEvents === 1 ? "1 Event" : `${numEvents} Events`
          }`}
        />

        <Divider />

        <CardContent className={classes.cardContent}>
          {addingNewTag && (
            <input
              tabIndex={0}
              placeholder="New Tag name..."
              ref={addingNewTagRef}
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onBlur={maybeCancelAddingNewTag}
            />
          )}

          {tagRows.map((tag) => (
            <MenuItem className={clsx(classes.menuItem)} key={`tag-row-${tag}`}>
              <span className="truncate">{tag}</span>
              <Checkbox
                checked={tag in activeTags}
                onChange={() => {
                  const newActiveTags =
                    tag in activeTags
                      ? _.omit(activeTags, tag)
                      : _.extend({}, activeTags, { [tag]: true });

                  setActiveTags(newActiveTags);
                }}
                value={tag in activeTags}
              />
            </MenuItem>
          ))}

          {!addingNewTag && (
            <div style={{ textAlign: "center" }}>
              <Button
                color="primary"
                onClick={() => setAddingNewTag(true)}
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                style={{ marginTop: theme.spacing(1) }}
              >
                Add New
              </Button>
            </div>
          )}
        </CardContent>

        <Divider />

        <CardActions className={classes.cardActions}>
          <Grid container className={classes.modalActions} spacing={1}>
            <Grid item>
              <Button
                className={classes.cancelBtn}
                color="inherit"
                size="small"
                title="Cancel"
                variant="contained"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="primary"
                size="small"
                title="Save"
                variant="contained"
                onClick={() => {
                  handleSave();
                }}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </CardActions>
      </Card>
    </ReactModal>
  );
};
